import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getAdminUsers, setUserStatus, deactivateUser } from "@/services/adminService";
import type { AdminUser, AdminUserStatus } from "@/types/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import {
  adminCard,
  adminTh,
  adminTd,
  adminBtnGhost,
  adminBtnDanger,
  adminIconBtn,
  statusBadge,
} from "@/pages/portal/admin/config/ui";

interface UsersSectionProps {
  ownerOnly?: boolean;
}

const ROLE_BADGE: Record<string, string> = {
  admin: "bg-rose-100 text-rose-700",
  owner: "bg-primary-100 text-primary-700",
  user:  "bg-secondary-100 text-secondary-700",
};

function relative(dateStr: string | null): string {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const d = Math.floor(diff / 86400000);
  if (d <= 0) return "Today";
  if (d === 1) return "Yesterday";
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

export default function UsersSection({ ownerOnly = false }: UsersSectionProps) {
  const { accessToken } = useAuth();
  const { data, setData, loading, error, reload } = useAdminResource(
    () => getAdminUsers(accessToken),
    [] as AdminUser[],
    [accessToken],
  );

  const [search, setSearch] = useState("");
  const [role, setRole]     = useState<"all" | AdminUser["role"]>(ownerOnly ? "owner" : "all");
  const [status, setStatus] = useState<"all" | AdminUserStatus>("all");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);

  const base = useMemo(
    () => (ownerOnly ? data.filter((u) => u.role === "owner") : data),
    [data, ownerOnly],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return base.filter((u) => {
      if (role !== "all" && u.role !== role) return false;
      if (status !== "all" && u.status !== status) return false;
      if (!q) return true;
      return [u.name, u.email, u.phone ?? ""].join(" ").toLowerCase().includes(q);
    });
  }, [base, search, role, status]);

  async function toggleStatus(u: AdminUser) {
    const next: AdminUserStatus = u.status === "active" ? "suspended" : "active";
    setData((prev) => prev.map((row) => (row.id === u.id ? { ...row, status: next } : row)));
    setBusyId(u.id);
    try {
      await setUserStatus(accessToken, u.id, next);
    } finally {
      setBusyId(null);
    }
  }

  async function confirmDeactivate() {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await deactivateUser(accessToken, deleteTarget.id);
      setData((prev) => prev.map((u) => (u.id === deleteTarget.id ? { ...u, status: "suspended" } : u)));
      setDeleteTarget(null);
    } finally {
      setBusyId(null);
    }
  }

  const title = ownerOnly ? "Business owners" : "Users";

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <AdminSearchInput value={search} onChange={setSearch} placeholder={`Search ${title.toLowerCase()}…`} />
        {!ownerOnly && (
          <select value={role} onChange={(e) => setRole(e.target.value as typeof role)} className="h-9 cursor-pointer rounded-lg border border-background-300 bg-background-100 px-3 text-xs text-foreground-700 outline-none focus:border-primary-400">
            <option value="all">All roles</option>
            <option value="owner">Owners</option>
            <option value="user">Users</option>
            <option value="admin">Admins</option>
          </select>
        )}
        <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className="h-9 cursor-pointer rounded-lg border border-background-300 bg-background-100 px-3 text-xs text-foreground-700 outline-none focus:border-primary-400">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
        </select>
        <span className="ml-auto text-xs text-foreground-500">
          {loading ? "Loading…" : `${filtered.length} of ${base.length}`}
        </span>
      </div>

      {error && <AdminDataError message={error} onRetry={reload} />}

      <div className={adminCard}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-group-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700">No {title.toLowerCase()} found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse">
              <thead className="border-b border-background-200">
                <tr>
                  <th className={adminTh}>Name</th>
                  <th className={adminTh}>Contact</th>
                  <th className={adminTh}>Role</th>
                  <th className={adminTh}>Listings</th>
                  <th className={adminTh}>Status</th>
                  <th className={adminTh}>Last login</th>
                  <th className={`${adminTh} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {filtered.map((u) => (
                  <tr key={u.id} className="hover:bg-background-100/40">
                    <td className={adminTd}>
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
                          {u.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                        </span>
                        <span className="font-semibold text-foreground-900">{u.name}</span>
                      </div>
                    </td>
                    <td className={adminTd}>
                      <a href={`mailto:${u.email}`} className="block text-xs text-primary-600 hover:underline">{u.email}</a>
                      {u.phone && <span className="text-[11px] text-foreground-400">{u.phone}</span>}
                    </td>
                    <td className={adminTd}>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${ROLE_BADGE[u.role]}`}>{u.role}</span>
                    </td>
                    <td className={`${adminTd} tabular-nums`}>{u.listings}</td>
                    <td className={adminTd}>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${statusBadge(u.status)}`}>{u.status}</span>
                    </td>
                    <td className={`${adminTd} text-xs text-foreground-500`}>{relative(u.lastLogin)}</td>
                    <td className={`${adminTd} text-right`}>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          disabled={busyId === u.id}
                          onClick={() => toggleStatus(u)}
                          className={adminIconBtn}
                          title={u.status === "active" ? "Suspend account" : "Activate account"}
                        >
                          <i className={`text-base ${u.status === "active" ? "ri-user-unfollow-line text-amber-600" : "ri-user-follow-line text-emerald-600"}`} />
                        </button>
                        <button type="button" onClick={() => setDeleteTarget(u)} className={adminIconBtn} title="Deactivate account">
                          <i className="ri-user-forbid-line text-base text-rose-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deleteTarget && (
        <AdminModal
          title="Deactivate account"
          subtitle={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteTarget(null)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={busyId === deleteTarget.id} onClick={confirmDeactivate} className={adminBtnDanger}>
                <i className="ri-user-forbid-line text-sm" /> Deactivate
              </button>
            </div>
          }
        >
          <p className="text-sm text-foreground-700">
            Deactivating <strong>{deleteTarget.name}</strong> blocks sign-in and hides their listings. Their data is retained and can be reactivated later.
          </p>
        </AdminModal>
      )}
    </div>
  );
}